import { Form, Formik, getIn } from 'formik';
import React, { useContext, useEffect, useRef, useState } from 'react';
import Moment from 'react-moment';
import * as Yup from 'yup';

import { useParams } from '@gatsbyjs/reach-router';
import FormGroup from '@material-ui/core/FormGroup';
import FormHelperText from '@material-ui/core/FormHelperText';
import FormLabel from '@material-ui/core/FormLabel';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { Alert } from '@material-ui/lab';

import DisplayAddressResult from '../../../components/display-address-result';
import SaveBackdrop from '../../../components/saveBackdrop';
import Screen from '../../../components/screen';
import SplashWait from '../../../components/splash-wait';
import { AppContext } from '../../../contexts/app-context';
import GlobalStyles from '../../../global-styles';
import API from '../../../utils/api';
import {
  addressValidation, apiReponseError, apiRequestError, canReadCompany, canReadProgram,
  canWriteCompany, canWriteProgram, filterCountryStates, generateUUID, getBreadcrumbMaxItems,
  getClientCompanyDetail, getDefaultAddressResult, getErrorObject, goToRoutePage,
  isFormReferenceValid, isNullOrWhiteSpace, isValidJSONString, openExternalLink,
  redirectRoutePage, setWindowTitle
} from '../../../utils/helper';
import {
  AddIcon, ChevronRightIcon, ClearAllIcon, ManageApiKeyIcon, MoreIcon, OpenInNewIcon,
  ValidateAddressIcon
} from '../../../utils/icons';
import {
  Box, Breadcrumbs, Button, Card, CardContent, CardHeader, FormControl, FormControlLabel, Grid,
  IconButton, InputLabel, LinearProgress, Link, List, ListItem, ListItemIcon, ListItemText, Menu,
  Paper, Select, Switch, TextField, Tooltip, Typography
} from '../../../utils/material-ui-core';
import ApiKeyCURD from '../../apikey/crud/apikey-crud';

// Default form value
const formDefaultValue = {
  active: false,
  address: {
    addressline1: "",
    addressline2: "",
    addressline3: "",
    country: "USA",
    locality1: "",
    locality2: "",
    postalCode: "",
    region1: "",
    region2: "",
  },
  changeProgram: false,
  clientCompanyKey: "",
  dcxSiteId: "",
  enableEnlistment: false,
  enlistmentSiteManageByAEM: false,
  enrollmentCount: 0,
  hsbPartnerId: "",
  lastModifiedBy: "",
  lastModifiedDateTime: "",
  name: "",
  parentKey: "",
  parentName: "",
  partnerType: "R",
  programCount: 0,
  routingName: "",
  shortName: "",
  folderId: 0,
  parentFolderId: 0,
  apiKey: "",
};

const formDefaultTouchValue = {
  active: true,
  addressline1: true,
  addressline2: false,
  changeProgram: true,
  clientCompanyKey: false,
  dcxSiteId: true,
  enableEnlistment: true,
  enlistmentSiteManageByAEM: true,
  enrollmentCount: false,
  hsbPartnerId: true,
  lastModifiedBy: false,
  lastModifiedDateTime: false,
  locality1: true,
  locality2: false,
  name: true,
  parentKey: true,
  parentName: false,
  partnerType: true,
  postalCode: true,
  programCount: false,
  region1: true,
  region2: false,
  routingName: true,
  shortName: true,
  folderId: false,
  parentFolderId: false,
};

// Form validation schema
const formValidationSchema = Yup.object().shape({
  name: Yup.string().trim().required("Name is required."),
  shortName: Yup.string().trim().required("Short name is required."),
  hsbPartnerId: Yup.number().positive("Code must be 4 digit postive number").required("Code is required"),
  parentKey: Yup.string().required("Parent is required"),
  address: Yup.object().shape({
    addressline1: Yup.string().trim().required("Address line 1 is required."),
    locality1: Yup.string().trim().required("City is required."),
    region1: Yup.string().trim().required("State is required."),
    postalCode: Yup.string().trim().required("Postal code is required."),
    country: Yup.string().trim().required("Country is required."),
  }),
  changeProgram: Yup.boolean().required("Change Program is required"),
  enableEnlistment: Yup.boolean().required("Enable enlistment is required"),
  routingName: Yup.string()
    .trim()
    .when(["enableEnlistment"], {
      is: (enableEnlistment) => {
        return enableEnlistment;
      },
      then: Yup.string().required("Routing name is required."),
    }),
  dcxSiteId: Yup.string().when(["enlistmentSiteManageByAEM"], {
    is: (enlistmentSiteManageByAEM) => {
      return enlistmentSiteManageByAEM;
    },
    then: Yup.string().required("AEM site id is required."),
  }),
  active: Yup.boolean().required("Status is required"),
  partnerType: Yup.string().trim().required("Type is required."),
});

const defaultAddressResultOutput: AddressResultOutput = getDefaultAddressResult();

const initialAddressResultOutput: AddressResultOutput = getDefaultAddressResult();

const ClientCompanyCRUD: React.FC = () => {
  const pageStyles = LocalPageStyles();
  const globalStyles = GlobalStyles();

  const { writeLog, refreshData, showAlert, appSetting, referenceLookups, userFunctionAccess } = useContext(AppContext);

  const { clientCompanyKey } = useParams();

  const formReference = useRef(null) as any;

  const [isPageInitialize, setIsPageInitialize] = useState<boolean>(false);
  const [addressValidationInProgress, setAddressValidationInProgress] = useState<boolean>(false);
  const [anchorElForOptionsMenu, setanchorElForOptionsMenu] = React.useState<null | HTMLElement>(null);
  const [clientCompanyAddressResultOutput, setClientCompanyAddressResultOutput] = useState<AddressResultOutput>(initialAddressResultOutput);
  const [initialFormValues, setInitialFormValues] = useState<ClientCompany>(formDefaultValue);
  const [loading, setLoading] = useState<boolean>(true);
  const [readonly, setReadonly] = useState<boolean>(false);
  const [limitedUpdates, setLimitedUpdates] = useState<boolean>(false);
  const [isNew, setIsNew] = useState<boolean>(false);
  const [states, setStates] = useState<State[]>([]);
  const [folderId, setFolderId] = useState<number>(0);
  const [saveButtonVisible, setSaveButtonVisible] = useState<boolean>(false);
  const [saveInProgress, setSaveInProgress] = useState<boolean>(false);

  const [manageApiKeyDialogInputOpen, setManageApiKeyDialogInputOpen] = useState<boolean>(false);

  // Address
  const resetAddressFields = () => {
    formReference.current.setFieldValue("address.addressline1", "");
    formReference.current.setFieldValue("address.addressline2", "");
    formReference.current.setFieldValue("address.locality1", "");
    formReference.current.setFieldValue("address.region1", "");
    formReference.current.setFieldValue("address.postalCode", "");
    formReference.current.setFieldTouched("address.addressline1", true);
    formReference.current.setFieldTouched("address.locality1", true);
    formReference.current.setFieldTouched("address.region1", true);
    formReference.current.setFieldTouched("address.postalCode", true);
    setTimeout(() => {
      formReference.current.validateForm();
    }, 100);
    setClientCompanyAddressResultOutput(defaultAddressResultOutput);
  };

  const handleAddressChanged = () => {
    closeAddressMessageStatus();
  };

  const useThisAddress = () => {
    formReference.current.setFieldValue("address", clientCompanyAddressResultOutput.address);

    if (clientCompanyAddressResultOutput.address.country === "US") {
      formReference.current.setFieldValue("address.country", "USA");
    } else if (clientCompanyAddressResultOutput.address.country === "CA") {
      formReference.current.setFieldValue("address.country", "CAN");
    }

    closeAddressMessageStatus();
  };

  const closeAddressMessageStatus = () => {
    const addressResultOutput = JSON.parse(JSON.stringify(clientCompanyAddressResultOutput));
    addressResultOutput.show = false;
    setClientCompanyAddressResultOutput({ ...addressResultOutput });
  };

  const showAddressValidationButton = () => {
    if (isFormReferenceValid(formReference)) {
      if (formReference.current.values.address.addressline1 !== "" && formReference.current.values.address.locality1 !== "" && formReference.current.values.address.region1 !== "" && formReference.current.values.address.postalCode !== "" && formReference.current.values.address.country !== "") {
        return true;
      }
    }
    return false;
  };

  const checkAddress = async () => {
    const payload = {
      address: {
        addressline1: formReference.current.values.address.addressline1,
        addressline2: formReference.current.values.address.addressline2,
        addressline3: "",
        country: formReference.current.values.address.country,
        locality1: formReference.current.values.address.locality1,
        locality2: "",
        postalCode: formReference.current.values.address.postalCode,
        region1: formReference.current.values.address.region1,
        region2: "",
      },
      allowPOBox: false,
      firmName: "",
    };

    const addressResultOutput = JSON.parse(JSON.stringify(clientCompanyAddressResultOutput));
    addressResultOutput.show = false;

    try {
      writeLog("Client Company Page", "Validating location address.", "", "");
      setAddressValidationInProgress(true);
      setClientCompanyAddressResultOutput(addressResultOutput);

      const apiResponse = await addressValidation(payload);
      if (apiResponse.success) {
        if (isValidJSONString(apiResponse.data.address)) {
          const address = apiResponse.data.address;

          if (address.valid) {
            addressResultOutput.valid = address.valid;
            addressResultOutput.message = address.friendlyMessage;
            addressResultOutput.address = address;
            addressResultOutput.addressLine = address.addressline1 + " " + address.addressline2;
            addressResultOutput.cityStatePostalCountry = address.locality1 + " " + address.region1 + " " + address.postalCode + " " + address.country;
            addressResultOutput.errorCodeTagged = address.errorCodeTagged;
            addressResultOutput.latitude = address.latitude;
            addressResultOutput.longitude = address.longitude;
            addressResultOutput.poBox = address.poBox;
            addressResultOutput.autoCorrect = address.autoCorrect;
            addressResultOutput.sameAddressLine = address.sameAddressLine;
            addressResultOutput.addressType = address.addressType;
          } else {
            addressResultOutput.valid = false;
            addressResultOutput.message = address.friendlyMessage;
          }
        } else {
          addressResultOutput.valid = false;
        }
      } else {
        addressResultOutput.valid = false;
        addressResultOutput.message = apiResponse.errorMessage;
        writeLog("Client Company Page", "Error : Validating location address failed. " + apiResponse.errorMessage, "", "");
      }

      addressResultOutput.show = true;
      setClientCompanyAddressResultOutput({ ...addressResultOutput });
      setAddressValidationInProgress(false);
    } catch (e) {
      addressResultOutput.valid = false;
      addressResultOutput.message = "Something was wrong in our system.";
      addressResultOutput.show = true;
      writeLog("Client Company Page", "Error : Validating location address failed & something was wrong in our system. ", "", "");
      setClientCompanyAddressResultOutput({ ...addressResultOutput });
      setAddressValidationInProgress(false);
    }
  };

  // Menu
  const handleAnchorMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setanchorElForOptionsMenu(event.currentTarget);
  };

  const handleAnchorElOptionsMenuClose = () => {
    setanchorElForOptionsMenu(null);
  };

  const gotoProgram = (programKey: string) => {
    if (!canReadProgram(userFunctionAccess)) {
      return;
    }

    writeLog("Client Company Page", "Jump to program", programKey, "program");
    goToRoutePage("onboarding/programs/" + programKey);
  };

  // Form

  const saveCompany = async (values: ClientCompany) => {
    if (!canWriteCompany(userFunctionAccess)) {
      showAlert({ message: "Access Denied.", severity: "error" });
      goToRoutePage("onboarding");
      return;
    }

    const enableEnlistment = values.enableEnlistment;
    let enlistmentSiteManageByAEM = values.enlistmentSiteManageByAEM;
    if (!enableEnlistment) {
      enlistmentSiteManageByAEM = false;
    }

    const payload = {
      company: {
        clientCompanyKey: values.clientCompanyKey,
        name: values.name,
        shortName: values.shortName,
        parentKey: values.parentKey,
        hsbPartnerId: values.hsbPartnerId,
        active: values.active,
        partnerType: values.partnerType,
        address: {
          addressline1: values.address.addressline1,
          addressline2: values.address.addressline2,
          addressline3: "",
          country: values.address.country,
          locality1: values.address.locality1,
          locality2: values.address.locality2,
          postalCode: values.address.postalCode,
          region1: values.address.region1,
          region2: values.address.region2,
        },
        enableEnlistment: enableEnlistment,
        routingName: enableEnlistment ? values.routingName : "",
        changeProgram: enableEnlistment ? values.changeProgram : false,
        enlistmentSiteManageByAEM: enlistmentSiteManageByAEM,
        dcxSiteId: enlistmentSiteManageByAEM ? values.dcxSiteId : "",
      },
    };
    try {
      setSaveInProgress(true);
      await API.post("/api/onboarding/clientcompanies/" + values.clientCompanyKey, payload)
        .then(async (response) => {
          writeLog("Client Company Page", "Successfully saved.", response.data.clientCompanyKey, "clientcompany");
          showAlert({ message: "Successfully saved.", severity: "success" });
          refreshData("companies");
          redirectRoutePage("onboarding/clientcompanies/" + response.data.clientCompanyKey);
          return;
        })
        .catch((error) => {
          setSaveInProgress(false);
          const errorList: ApiResponseErrorObject[] = getErrorObject(error, "company.");
          let isErrorAssignedAtFieldLevel = true;
          try {
            errorList.forEach(function (error) {
              formReference.current.setFieldError(error.title, error.message.join(" "));
            });
          } catch (e) {
            isErrorAssignedAtFieldLevel = false;
          }
          isErrorAssignedAtFieldLevel = false;
          if (errorList.length <= 0 || !isErrorAssignedAtFieldLevel) {
            showAlert({
              message: apiReponseError(error, "Failed to save client company data."),
              severity: "error",
            });
          }

          writeLog("Client Company Page", "Failed to save client company data.", clientCompanyKey, "clientcompany");
        });
    } catch (e) {
      setSaveInProgress(false);
      writeLog("Client Company Page", "Error : Failed to save & something went wrong in our system.", "", "");
      showAlert({
        message: apiRequestError(e, "Something went wrong in our system."),
        severity: "error",
      });
    }
  };

  const handleCountryChanged = (event: any) => {
    setStates(filterCountryStates(referenceLookups.countries, event.target.value));
    formReference.current.setFieldValue("address.region1", "");
  };

  const openManageApiKey = () => {
    setManageApiKeyDialogInputOpen(true);
  };

  const confirmManageApiKey = (confirmOutput: ConfirmOutput) => {
    setManageApiKeyDialogInputOpen(false);
  };

  useEffect(() => {
    writeLog("Client Company Page", "", "", "");
    setWindowTitle("Onboarding - Client Company");
    window.scrollTo(0, 0);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const initialPageAsync = async () => {
      if (isPageInitialize) {
        return true;
      }

      if (!canReadCompany(userFunctionAccess)) {
        writeLog("Client Company Page", "Access denied.", "", "");
        showAlert({ message: "Access Denied", severity: "error" });
        goToRoutePage("Home");
        return;
      }

      if (isNullOrWhiteSpace(clientCompanyKey)) {
        writeLog("Client Company Page", "Client company key missing.", "", "");
        showAlert({
          message: "Client company key missing.",
          severity: "error",
        });
        goToRoutePage("onboarding");
        return;
      }

      if (clientCompanyKey === referenceLookups.newRecordDefaultId) {
        if (!canWriteCompany(userFunctionAccess)) {
          writeLog("Client Company Page", "Edit access denied.", "", "");
          showAlert({ message: "Access Denied.", severity: "error" });
          goToRoutePage("onboarding");
          return;
        }

        setInitialFormValues({
          ...formDefaultValue,
          clientCompanyKey: referenceLookups.newRecordDefaultId,
        });
        setStates(filterCountryStates(referenceLookups.countries, formDefaultValue.address.country));
        setReadonly(false);
        setIsNew(true);
        setIsPageInitialize(true);
        setLoading(false);
        writeLog("Client Company Page", "New client company form opened.", "", "");
        return;
      }

      const apiResponse = await getClientCompanyDetail(clientCompanyKey);
      if (apiResponse.success) {
        const data = apiResponse.data;
        setInitialFormValues(data.clientCompany as ClientCompany);
        setFolderId(data.clientCompany.folderId);
        setStates(filterCountryStates(referenceLookups.countries, data.clientCompany.address.country));
        setReadonly(!canWriteCompany(userFunctionAccess));
        if (apiResponse.data.clientCompany.enrollmentCount > 0) {
          setLimitedUpdates(true);
        }
        setIsNew(false);
        setIsPageInitialize(true);
        setLoading(false);
        writeLog("Client Company Page", "Edit client company form opened.", clientCompanyKey, "clientcompany");
      } else {
        writeLog("Client Company Page", "Error : " + apiResponse.errorMessage, "", "");
        showAlert({ message: apiResponse.errorMessage, severity: "error" });
        goToRoutePage("onboarding");
        return;
      }
    };

    initialPageAsync();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientCompanyKey, referenceLookups, userFunctionAccess]);

  return (
    <>
      {loading ? (
        <SplashWait />
      ) : (
        <Screen>
          <ApiKeyCURD open={manageApiKeyDialogInputOpen} apiKey={initialFormValues.apiKey} selectedClientCompanyKey={clientCompanyKey} onCallback={confirmManageApiKey} />

          <SaveBackdrop open={saveInProgress} />

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Grid container>
                <Grid item xs={10} sm={10} md={10} lg={10}>
                  <Box className={globalStyles.bottomSpacer}>
                    <Breadcrumbs aria-label="breadcrumb" maxItems={getBreadcrumbMaxItems(3)}>
                      <Link color="inherit" onClick={() => goToRoutePage("home")} className={globalStyles.link}>
                        Home
                      </Link>
                      <Link color="inherit" onClick={() => goToRoutePage("onboarding")} className={globalStyles.link}>
                        Onboarding
                      </Link>
                      <Link color="inherit" onClick={() => goToRoutePage("onboarding\\companies")} className={globalStyles.link}>
                        Client Company
                      </Link>
                      <Typography color="textPrimary">{isNew ? "New" : !readonly ? "Edit" : "View"}</Typography>
                    </Breadcrumbs>
                  </Box>
                </Grid>
                <Grid item xs={2} sm={2} md={2} lg={2}>
                  {!isNew ? (
                    <Box display="flex" justifyContent="flex-end" width={1}>
                      <Button color="secondary" aria-controls="action-menu" aria-haspopup="true" onClick={handleAnchorMenuClick}>
                        <MoreIcon />
                      </Button>
                      <Menu elevation={2} id="action-menu" className={globalStyles.rightsideMenu} anchorEl={anchorElForOptionsMenu} keepMounted open={Boolean(anchorElForOptionsMenu)} onClose={handleAnchorElOptionsMenuClose}>
                        <ListItem
                          onClick={() => {
                            openExternalLink(appSetting.meshifyUrl + "/admin#/folders/" + folderId);
                            handleAnchorElOptionsMenuClose();
                          }}
                        >
                          <ListItemText primary="Meshify Admin" />
                          <ListItemIcon className={pageStyles.listItemIcon}>
                            <OpenInNewIcon color="secondary" />
                          </ListItemIcon>
                        </ListItem>
                        <ListItem
                          onClick={() => {
                            openExternalLink(appSetting.meshifyUrl + "/dashboard3#folders?folderId=" + folderId);
                            handleAnchorElOptionsMenuClose();
                          }}
                        >
                          <ListItemText primary="Meshify Dashboard" />
                          <ListItemIcon className={pageStyles.listItemIcon}>
                            <OpenInNewIcon color="secondary" />
                          </ListItemIcon>
                        </ListItem>

                        <ListItem
                          onClick={() => {
                            openManageApiKey();
                            handleAnchorElOptionsMenuClose();
                          }}
                        >
                          <ListItemText primary="Manage API Key" />
                          <ListItemIcon className={pageStyles.listItemIcon}>
                            <ManageApiKeyIcon color="secondary" />
                          </ListItemIcon>
                        </ListItem>
                      </Menu>
                    </Box>
                  ) : null}
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} sm={12} md={8} lg={9} className={globalStyles.formikContainer}>
              <Paper elevation={0} className={pageStyles.inputPaperContainer}>
                <Grid container>
                  {limitedUpdates ? (
                    <>
                      <Grid item xs={12}>
                        <Alert severity="info">{"Please note that limited updates can be made to this client company since " + initialFormValues.enrollmentCount + " participants have already been enrolled."}</Alert>
                      </Grid>
                      <Grid item xs={12}>
                        <Box p={3}></Box>
                      </Grid>
                    </>
                  ) : (
                    <Grid item xs={12}>
                      <Box p={1}></Box>
                    </Grid>
                  )}

                  <Grid item xs={12} className={pageStyles.formContainer}>
                    <Formik
                      innerRef={formReference}
                      initialValues={initialFormValues}
                      validationSchema={formValidationSchema}
                      validateOnMount={!isNew}
                      initialTouched={!isNew ? formDefaultTouchValue : {}}
                      validateOnBlur={true}
                      validateOnChange={true}
                      onSubmit={(values: ClientCompany) => {
                        saveCompany(values);
                      }}
                    >
                      {({ values, errors, handleChange, handleSubmit, touched, handleBlur, isSubmitting, setFieldValue }) => (
                        <Form
                          onChange={() => {
                            setSaveButtonVisible(true);
                          }}
                        >
                          <Grid container spacing={3}>
                            {/* Key */}

                            <Grid item xs={12} sm={8} md={8} lg={4}>
                              <TextField disabled={readonly || limitedUpdates} id="name" label="Name" value={values.name} onChange={handleChange} onBlur={handleBlur} error={getIn(errors, "name")} helperText={getIn(errors, "name")} autoFocus={isNew} />
                            </Grid>
                            <Grid item xs={9} sm={4} md={4} lg={2}>
                              <TextField disabled={readonly || limitedUpdates} id="shortName" name="shortName" label="Short Name" value={values.shortName} onChange={handleChange} onBlur={handleBlur} error={getIn(errors, "shortName")} helperText={getIn(errors, "shortName")} />
                            </Grid>
                            <Grid item xs={12} sm={8} md={8} lg={4}>
                              <FormControl required className={globalStyles.formControl} fullWidth={true} error={touched.parentKey && Boolean(errors.parentKey)}>
                                <InputLabel id="parent-id-label" shrink={true}>
                                  Parent
                                </InputLabel>
                                <Select disabled={readonly || limitedUpdates} native labelId="parent-key-label" id="parentKey" name="parentKey" value={values.parentKey} onChange={handleChange} label="Parent" error={getIn(errors, "parentKey")}>
                                  {initialFormValues.clientCompanyKey === referenceLookups.newRecordDefaultId ? (
                                    <>
                                      <option value=""></option>
                                    </>
                                  ) : (
                                    <></>
                                  )}
                                  {referenceLookups.parentCompanies.map((parentCompany, index) => (
                                    <option value={parentCompany.parentKey} key={index}>
                                      {parentCompany.name}
                                    </option>
                                  ))}
                                </Select>
                                <FormHelperText error>{getIn(errors, "parentKey")}</FormHelperText>
                              </FormControl>
                            </Grid>

                            <Grid item xs={9} sm={4} md={4} lg={2}>
                              <TextField disabled={readonly || limitedUpdates} id="hsbPartnerId" name="hsbPartnerId" label="Code" type="number" value={values.hsbPartnerId} onChange={handleChange} onBlur={handleBlur} error={getIn(errors, "hsbPartnerId")} helperText={getIn(errors, "hsbPartnerId")} />
                            </Grid>
                            <Grid item xs={6} sm={6} md={4} lg={3}>
                              <FormControl className={globalStyles.formControl} fullWidth={true}>
                                <FormLabel component="legend" className={globalStyles.checkBoxLabel}>
                                  Active
                                </FormLabel>
                                <FormGroup>
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        disabled={readonly}
                                        id="active"
                                        name="active"
                                        checked={values.active}
                                        onChange={(ev, checked) => {
                                          setFieldValue("active", checked);
                                        }}
                                      />
                                    }
                                    label={values.active ? "Yes" : "No"}
                                    labelPlacement="end"
                                  />
                                </FormGroup>
                              </FormControl>
                            </Grid>

                            <Grid item xs={6} sm={6} md={4} lg={3}>
                              <FormControl required className={globalStyles.formControl} fullWidth={true} error={Boolean(errors.partnerType)}>
                                <InputLabel id="type-label" shrink={true}>
                                  Type
                                </InputLabel>
                                <Select disabled={readonly || limitedUpdates} native labelId="type-label" id="partnerType" value={values.partnerType} onChange={handleChange} label="Type" error={touched.partnerType && Boolean(errors.partnerType)}>
                                  <option value={"D"}>Direct</option>
                                  <option value={"R"}>Reinsurance</option>
                                </Select>
                                <FormHelperText>{errors.partnerType}</FormHelperText>
                              </FormControl>
                            </Grid>

                            <Grid item xs={12}></Grid>

                            {/* Location */}

                            <Grid item xs={12}>
                              <Box className={globalStyles.topic}>
                                {addressValidationInProgress ? <LinearProgress color="secondary" /> : null}
                                <Grid container>
                                  <Grid item xs={8}>
                                    Location
                                  </Grid>
                                  <Grid item xs={4}>
                                    <Box display="flex" justifyContent="flex-end" width={1}>
                                      {!readonly ? (
                                        <React.Fragment>
                                          {!addressValidationInProgress ? (
                                            <>
                                              {showAddressValidationButton() ? (
                                                <Tooltip arrow title="Validate address">
                                                  <IconButton onClick={checkAddress}>
                                                    <ValidateAddressIcon color="secondary" />
                                                  </IconButton>
                                                </Tooltip>
                                              ) : null}
                                            </>
                                          ) : null}
                                          <Typography variant="body1">&nbsp;&nbsp;&nbsp;</Typography>
                                          <Tooltip arrow title="Clear address">
                                            <IconButton onClick={resetAddressFields}>
                                              <ClearAllIcon color="error" />
                                            </IconButton>
                                          </Tooltip>
                                        </React.Fragment>
                                      ) : null}
                                    </Box>
                                  </Grid>
                                </Grid>
                              </Box>
                            </Grid>
                            {clientCompanyAddressResultOutput.show ? (
                              <Grid item xs={12}>
                                <DisplayAddressResult addressResultOutput={clientCompanyAddressResultOutput} currentAddress={values.address} callbackOnClose={closeAddressMessageStatus} useThisAddressButton={true} callbackOnUseThisAddress={useThisAddress} overrideValidationAddress={false} callbackOnOverrideValidationAddress={() => { }} />
                              </Grid>
                            ) : null}

                            <Grid item xs={12} sm={6} md={6} lg={6}>
                              <TextField
                                disabled={readonly}
                                id="addressline1"
                                name="address.addressline1"
                                label="Address Line 1"
                                value={values.address.addressline1}
                                onChange={(e) => {
                                  handleChange(e);
                                  handleAddressChanged();
                                }}
                                onBlur={handleBlur}
                                error={getIn(errors, "address.addressline1")}
                                helperText={getIn(errors, "address.addressline1")}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={6} lg={6}>
                              <TextField
                                disabled={readonly}
                                id="address.addressline2"
                                name="address.addressline2"
                                label="Address Line 2"
                                value={values.address.addressline2}
                                onChange={(e) => {
                                  handleChange(e);
                                  handleAddressChanged();
                                }}
                                onBlur={handleBlur}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={6} lg={3}>
                              <TextField
                                disabled={readonly}
                                id="address.locality1"
                                name="address.locality1"
                                label="City"
                                value={values.address.locality1}
                                onChange={(e) => {
                                  handleChange(e);
                                  handleAddressChanged();
                                }}
                                onBlur={handleBlur}
                                error={Boolean(getIn(errors, "address.locality1"))}
                                helperText={getIn(errors, "address.locality1")}
                              />
                            </Grid>
                            <Grid item xs={6} sm={6} md={6} lg={3}>
                              <FormControl className={globalStyles.formControl} fullWidth={true} error={Boolean(getIn(errors, "address.region1"))}>
                                <InputLabel id="state-label" shrink={true}>
                                  State
                                </InputLabel>
                                <Select
                                  disabled={readonly}
                                  native
                                  labelId="state-label"
                                  id="address.region1"
                                  name="address.region1"
                                  value={values.address.region1}
                                  onChange={(e) => {
                                    handleChange(e);
                                    handleAddressChanged();
                                  }}
                                  label="State"
                                  error={Boolean(getIn(errors, "address.region1"))}
                                >
                                  <option value={""}></option>
                                  {states.map((state, index) => (
                                    <option value={state.code} key={index}>
                                      {state.name}
                                    </option>
                                  ))}
                                </Select>

                                <FormHelperText error>{getIn(errors, "address.region1")}</FormHelperText>
                              </FormControl>
                            </Grid>
                            <Grid item xs={6} sm={6} md={6} lg={3}>
                              <TextField
                                disabled={readonly}
                                id="address.postalCode"
                                name="address.postalCode"
                                label="Postal Code"
                                value={values.address.postalCode}
                                onChange={(e) => {
                                  handleChange(e);
                                  handleAddressChanged();
                                }}
                                onBlur={handleBlur}
                                error={Boolean(getIn(errors, "address.postalCode"))}
                                helperText={getIn(errors, "address.postalCode")}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={6} lg={3}>
                              <FormControl className={globalStyles.formControl} fullWidth={true} error={Boolean(getIn(errors, "address.country"))}>
                                <InputLabel id="country-label" shrink={true}>
                                  Country
                                </InputLabel>
                                <Select
                                  disabled={readonly}
                                  native
                                  labelId="country-label"
                                  id="address.country"
                                  name="address.country"
                                  value={values.address.country}
                                  onChange={(e) => {
                                    handleAddressChanged();
                                    handleChange(e);
                                    handleCountryChanged(e);
                                  }}
                                  label="Country"
                                  error={Boolean(getIn(errors, "address.country"))}
                                >
                                  {referenceLookups.countries.map((country, index) => (
                                    <option value={country.code} key={index}>
                                      {country.name}
                                    </option>
                                  ))}
                                </Select>
                                <FormHelperText error>{getIn(errors, "address.country")}</FormHelperText>
                              </FormControl>
                            </Grid>

                            {/* Enlistment */}

                            <Grid item xs={12}>
                              <Box className={globalStyles.topic}>Enlistment</Box>
                            </Grid>
                            <Grid item xs={12} sm={6} md={6} lg={3}>
                              <FormControl className={globalStyles.formControl} fullWidth={true}>
                                <FormLabel component="legend" className={globalStyles.checkBoxLabel}>
                                  Enable Enlistment Site?
                                </FormLabel>
                                <FormGroup>
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        disabled={readonly}
                                        id="enableEnlistment"
                                        name="enableEnlistment"
                                        checked={values.enableEnlistment}
                                        onChange={(ev, checked) => {
                                          setFieldValue("enableEnlistment", checked);
                                          if (!checked) {
                                            setFieldValue("routingname", "");
                                            setFieldValue("enlistmentSiteManageByAEM", false);
                                          }
                                        }}
                                      />
                                    }
                                    label={values.enableEnlistment ? "Yes" : "No"}
                                    labelPlacement="end"
                                  />
                                </FormGroup>
                              </FormControl>
                            </Grid>

                            {values.enableEnlistment ? (
                              <>
                                <Grid item xs={12} sm={6} md={6} lg={3}>
                                  <TextField disabled={readonly} id="routingName" name="routingName" label="Site Routing Name" value={values.routingName} onChange={handleChange} onBlur={handleBlur} error={touched.routingName && Boolean(errors.routingName)} helperText={errors.routingName && touched.routingName ? errors.routingName : null} />
                                </Grid>
                                <Grid item xs={12} sm={6} md={6} lg={4}>
                                  <FormControl className={globalStyles.formControl} fullWidth={true}>
                                    <FormLabel component="legend" className={globalStyles.checkBoxLabel}>
                                      Allow Enlistment Admin to Change Program
                                    </FormLabel>
                                    <FormGroup>
                                      <FormControlLabel
                                        control={
                                          <Switch
                                            disabled={readonly}
                                            id="changeProgram"
                                            name="changeProgram"
                                            checked={values.changeProgram}
                                            onChange={(ev, checked) => {
                                              setFieldValue("changeProgram", checked);
                                            }}
                                          />
                                        }
                                        label={values.changeProgram ? "Yes" : "No"}
                                        labelPlacement="end"
                                      />
                                    </FormGroup>
                                  </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6} md={6} lg={3}>
                                  <FormControl className={globalStyles.formControl} fullWidth={true}>
                                    <FormLabel component="legend" className={globalStyles.checkBoxLabel}>
                                      Site manage by AEM?
                                    </FormLabel>
                                    <FormGroup>
                                      <FormControlLabel
                                        control={
                                          <Switch
                                            disabled={readonly}
                                            id="enlistmentSiteManageByAEM"
                                            name="enlistmentSiteManageByAEM"
                                            checked={values.enlistmentSiteManageByAEM}
                                            onChange={(ev, checked) => {
                                              setFieldValue("enlistmentSiteManageByAEM", checked);
                                              if (checked) {
                                                if (values.dcxSiteId === "") {
                                                  const newId: string = generateUUID();
                                                  formReference.current.setFieldValue("dcxSiteId", newId);
                                                }
                                              }
                                            }}
                                          />
                                        }
                                        label={values.enlistmentSiteManageByAEM ? "Yes" : "No"}
                                        labelPlacement="end"
                                      />
                                    </FormGroup>
                                  </FormControl>
                                </Grid>

                                {values.enlistmentSiteManageByAEM ? (
                                  <Grid item xs={12} sm={12} md={12} lg={6}>
                                    <TextField required disabled={true} id="dcxSiteId" name="dcxSiteId" label="AEM Site Id" value={values.dcxSiteId} onChange={handleChange} onBlur={handleBlur} error={touched.dcxSiteId && Boolean(errors.dcxSiteId)} helperText={errors.dcxSiteId && touched.dcxSiteId ? errors.dcxSiteId : null} />
                                  </Grid>
                                ) : null}
                              </>
                            ) : null}

                            <Grid item xs={12}>
                              <Box p={1}></Box>
                            </Grid>
                          </Grid>

                          <Box p="7">&nbsp;</Box>

                          <Box p={3} pt={3} display="flex" justifyContent="center">
                            {!readonly ? (
                              <Button disabled={!saveButtonVisible} id="button-save" variant="contained" color="secondary" onClick={() => formReference.current.handleSubmit()} className={globalStyles.buttonDefault}>
                                {isNew ? "Save" : "Update"}
                              </Button>
                            ) : null}
                            <Button id="button-cancel" color="default" className={globalStyles.buttonDefault} onClick={() => goToRoutePage("onboarding/companies")}>
                              <u>{!readonly ? "Cancel" : "Back"}</u>
                            </Button>
                          </Box>
                        </Form>
                      )}
                    </Formik>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={12} md={4} lg={3}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={4} md={12} lg={12}>
                  <Card elevation={0} className={globalStyles.rightCardContainer}>
                    <CardHeader title="" subheader="INCLUDES" />
                    <CardContent>
                      <Grid container>
                        <Grid item xs={6}  >
                          <Box display="flex" flexDirection="column" justifyItems='center' alignItems='center' textAlign="center" >
                            <Box>
                              <Typography variant="h6" className="count"  >
                                {!isNew ? initialFormValues.programCount : " "}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" className={globalStyles.toggleFontColor}>
                                Programs
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                        <Grid item xs={6}  >
                          <Box display="flex" flexDirection="column" justifyItems='center' alignItems='center' textAlign="center" >
                            <Box>
                              <Typography variant="h6" className="count"  >
                                {!isNew ? initialFormValues.enrollmentCount : " "}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" className={globalStyles.toggleFontColor}>
                                Enrollments
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                  <Box p="3">&nbsp;</Box>
                </Grid>

                <Grid item xs={12} sm={8} md={12} lg={12}>
                  <Card elevation={0} className={globalStyles.rightCardContainer}>
                    <CardHeader
                      title=""
                      subheader="ASSOCIATED PROGRAMS"
                      action={
                        <>
                          {!readonly && !isNew && !saveButtonVisible && canWriteProgram(userFunctionAccess) && initialFormValues.active ? (
                            <Tooltip arrow title={"Associated Programs"}>
                              <IconButton
                                onClick={() => {
                                  goToRoutePage("onboarding/programs/" + referenceLookups.newRecordDefaultId + "/" + initialFormValues.clientCompanyKey);
                                }}
                              >
                                <AddIcon color={"secondary"} />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                        </>
                      }
                    />
                    <CardContent>
                      <Box className={pageStyles.programContent}>
                        <List component="nav">
                          {referenceLookups.programs
                            .filter((program) => program.hsbPartnerId === initialFormValues.hsbPartnerId)
                            .map((program, index) => {
                              return (
                                <ListItem
                                  button
                                  component="a"
                                  key={index}
                                  onClick={() => {
                                    gotoProgram(program.programKey);
                                  }}
                                >
                                  <ListItemText primary={program.programName} />
                                  {!saveButtonVisible && canReadProgram(userFunctionAccess) ? (
                                    <ListItemIcon className={pageStyles.listItemIcon}>
                                      <ChevronRightIcon fontSize="medium" />
                                    </ListItemIcon>
                                  ) : null}
                                </ListItem>
                              );
                            })}
                        </List>
                      </Box>
                    </CardContent>
                  </Card>
                  <Box p="3">&nbsp;</Box>
                  <Box p="3">&nbsp;</Box>
                </Grid>

                <Grid item xs={12} sm={12} md={12} lg={12}>
                  <Card elevation={0} className={globalStyles.rightCardContainer}>
                    <CardHeader title="" subheader="AUDIT" />
                    <CardContent>
                      <Grid container>
                        <Grid item xs={12}  >
                          <Box display="flex" flexDirection="column" justifyItems='center' alignItems='center' textAlign="center" >
                            <Box>
                              <Typography variant="h6" className="text"  >
                                {!isNew && initialFormValues.lastModifiedBy !== "" ? initialFormValues.lastModifiedBy : "  "}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" className={globalStyles.toggleFontColor}>
                                Last Update By
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                        <Grid item xs={12}  >&nbsp;</Grid>
                        <Grid item xs={12}  >
                          <Box display="flex" flexDirection="column" justifyItems='center' alignItems='center' textAlign="center" >
                            <Box>
                              <Typography variant="h6" className="text"  >
                                {!isNew && initialFormValues.lastModifiedDateTime !== "" ? <Moment date={initialFormValues.lastModifiedDateTime} format="M/D/YYYY - h:mm:ss A z" /> : "  "}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" className={globalStyles.toggleFontColor}>
                                Last Updated On
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                  <Box p="3">&nbsp;</Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Screen>
      )}
    </>
  );
};

const LocalPageStyles = makeStyles((theme: Theme) =>
  createStyles({
    selectEmpty: {
      marginTop: theme.spacing(2),
    },
    inputMinWidth: {
      maxWidth: "250px",
    },
    formContainer: {
      maxWidth: "1200px",
    },
    countContainer: {
      justifyItems: "center",
      cursor: "default",
    },
    listItemIcon: {
      justifyContent: "flex-end",
    },
    inputPaperContainer: {
      padding: theme.spacing(0),
      backgroundColor: "transparent",
    },
    programDialogContent: {
      minHeight: "300px",
      minWidth: "350px",
      width: "100%",
    },
    programContent: {
      maxHeight: "250px",
      overflow: "auto",
    },
  })
);

export default ClientCompanyCRUD;
